import { useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'
import DiscountRateBuilder from 'components/DiscountRateBuilder'
import { adminEditAccount } from 'api/accounts'
import { ApprovedAccount } from 'types/accountTypes'

interface EditDiscountRateFormProps {
  data: ApprovedAccount
  onSaved?: () => void
}

const EditDiscountRateForm = ({ data, onSaved }: EditDiscountRateFormProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')

  const [email, setEmail] = useState(data.email ?? '')
  const [firstName, setFirstName] = useState(data.firstName ?? '')
  const [lastName, setLastName] = useState(data.lastName ?? '')
  const [companyName, setCompanyName] = useState(data.companyName ?? '')
  const [isTaxable, setIsTaxable] = useState<string>(
    data.isTaxable === true ? 'true' : data.isTaxable === false ? 'false' : ''
  )
  const discountRatesRef = useRef<Record<string, number>>(
    data.discountRates ?? {}
  )

  const handleSave = async () => {
    setError('')
    setSuccessMessage('')
    setIsLoading(true)

    const updates: Record<string, any> = {}
    if (email && email !== data.email) updates.email = email
    if (firstName !== data.firstName) updates.firstName = firstName
    if (lastName !== data.lastName) updates.lastName = lastName
    if (companyName && companyName !== data.companyName)
      updates.companyName = companyName
    if (isTaxable !== '') updates.isTaxable = isTaxable === 'true'
    if (Object.keys(discountRatesRef.current).length > 0) {
      updates.discountRates = discountRatesRef.current
    }

    if (Object.keys(updates).length === 0) {
      setError('No changes to save.')
      setIsLoading(false)
      return
    }

    const result = await adminEditAccount(data.id, updates)
    if (result.success) {
      setSuccessMessage(result.message)
      onSaved?.()
    } else {
      setError(result.message)
    }
    setIsLoading(false)
  }

  return (
    <Paper
      variant='outlined'
      sx={{ borderRadius: 2, overflow: 'hidden', mt: 3 }}
    >
      {/* Header */}
      <Box
        px={2.5}
        py={1.5}
        bgcolor='grey.50'
        borderBottom='1px solid'
        borderColor='divider'
      >
        <Typography variant='subtitle1' fontWeight={600}>
          Edit Account
        </Typography>
      </Box>

      <Box px={2.5} py={2.5}>
        {error && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {successMessage && (
          <Alert severity='success' sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

        {/* Basic Info */}
        <Typography
          variant='overline'
          color='text.secondary'
          display='block'
          mb={1}
        >
          Basic Info
        </Typography>
        <Stack spacing={2} mb={3}>
          <TextField
            label='Email'
            size='small'
            fullWidth
            value={email}
            onChange={e => setEmail(e.target.value)}
          />
          <Stack direction='row' spacing={2}>
            <TextField
              label='First Name'
              size='small'
              fullWidth
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
            />
            <TextField
              label='Last Name'
              size='small'
              fullWidth
              value={lastName}
              onChange={e => setLastName(e.target.value)}
            />
          </Stack>
          <Stack direction='row' spacing={2}>
            <TextField
              label='Company Name'
              size='small'
              sx={{ flex: 2 }}
              value={companyName}
              onChange={e => setCompanyName(e.target.value)}
            />
            <FormControl size='small' sx={{ flex: 1, minWidth: 100 }}>
              <InputLabel>Taxable</InputLabel>
              <Select
                value={isTaxable}
                label='Taxable'
                onChange={e => setIsTaxable(e.target.value)}
              >
                <MenuItem value='true'>Yes</MenuItem>
                <MenuItem value='false'>No</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Stack>

        <Divider sx={{ mb: 2.5 }} />

        {/* Discount Rates */}
        <Typography
          variant='overline'
          color='text.secondary'
          display='block'
          mb={1}
        >
          Discount Rates
        </Typography>
        <DiscountRateBuilder
          initialRates={data.discountRates}
          onChange={rates => {
            discountRatesRef.current = rates
          }}
        />

        <Button
          variant='contained'
          onClick={handleSave}
          disabled={isLoading}
          startIcon={
            isLoading ? <CircularProgress size={18} /> : <SaveOutlinedIcon />
          }
          sx={{ mt: 2.5 }}
        >
          {isLoading ? 'Saving…' : 'Save Changes'}
        </Button>
      </Box>
    </Paper>
  )
}

export default EditDiscountRateForm
